import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Brain, CheckCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

import { API_URL } from '../config';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-[#131313] border border-white/10 p-8">
            <h1 className="font-heading font-bold text-2xl uppercase text-white mb-4">Invalid Link</h1>
            <p className="text-[#8892B0] mb-6">This reset link is invalid or has expired.</p>
            <Link
              to="/forgot-password"
              className="inline-block px-8 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors whitespace-nowrap"
              data-testid="request-new-link"
            >
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Brain size={40} weight="duotone" className="text-[#007AFF]" />
            <span className="font-heading font-black text-2xl tracking-tight text-white uppercase">
              Trader Reset AI
            </span>
          </Link>
        </div>

        <div className="bg-[#131313] border border-white/10 p-8" data-testid="reset-password-form">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#34C759]/10 flex items-center justify-center">
                <CheckCircle size={32} className="text-[#34C759]" />
              </div>
              <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
                Password Reset
              </h1>
              <p className="text-[#8892B0] mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Link
                to="/login"
                className="inline-block px-8 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors whitespace-nowrap"
                data-testid="go-to-login"
              >
                Sign In
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading font-bold text-2xl uppercase text-white mb-2 text-center">
                New Password
              </h1>
              <p className="text-[#8892B0] text-center mb-8">
                Enter your new password below
              </p>

              {error && (
                <div className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="password" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4 pr-12"
                      placeholder="At least 6 characters"
                      required
                      data-testid="reset-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8892B0] hover:text-white"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4"
                    placeholder="Repeat your password"
                    required
                    data-testid="reset-confirm-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  data-testid="reset-submit"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
